// 3rd party
import * as React from 'react';

// Shared UI
import { LocalizedText, useLocalization } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkCard } from 'uitk-react-cards';
import { UitkIcon } from 'uitk-react-icons';
import { UitkFakeInput } from 'uitk-react-input';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading } from 'uitk-react-text';

// Components
import { CopyButton } from 'components/quicklinks';

// Models
import { Quicklink } from 'models';
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';
import { LinkType } from 'models/analytics/enums';

interface ConfiguredQuicklinkV2Props extends Quicklink {
  index: number;
  analyticsTrackEvents?: (rffr: string, link: LinkAnalyticsMicroMessageContent) => void;
}

function ConfiguredQuicklinkV2(props: ConfiguredQuicklinkV2Props): JSX.Element {
  const { label, title, url, link, code, index, analyticsTrackEvents } = props;

  const l10n = useLocalization();

  return (
    <UitkSpacing margin={{ blockend: 'four' }}>
      <UitkCard padded={true}>
        <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
          <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
            <div>
              <UitkHeading tag="h2" size={5}>
                {title}
              </UitkHeading>

              <UitkLink inline={true}>
                <a href={url} target="_blank" rel="noreferrer">
                  <LocalizedText message="product.quicklink.configured.link.preview" />
                  <UitkIcon name="open_in_new" />
                </a>
              </UitkLink>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
            <UitkLayoutGrid columns={{ small: 1, large: 2 }} space="four">
              <UitkLayoutGridItem>
                <div>
                  <UitkLayoutFlex
                    className="QuicklinkInputs"
                    space="two"
                    data-stid={`copy-preconfigured-link-${index}`}
                  >
                    <UitkFakeInput
                      id={`link-${label}`}
                      label={l10n.formatText('product.quicklink.form.link')}
                      value={link}
                    />

                    <CopyButton
                      value={link}
                      onCopyClick={() =>
                        analyticsTrackEvents('affhub_copy_link.selected', {
                          linkType: LinkType.PREDEFINED,
                          linkIndex: index,
                          linkTargetUrl: link,
                        })
                      }
                    />
                  </UitkLayoutFlex>
                </div>
              </UitkLayoutGridItem>

              <UitkLayoutGridItem>
                <div>
                  <UitkLayoutFlex
                    className="QuicklinkInputs"
                    space="two"
                    data-stid={`copy-preconfigured-code-${index}`}
                  >
                    <UitkFakeInput
                      id={`code-${label}`}
                      label={l10n.formatText('product.quicklink.form.code')}
                      value={code}
                    />

                    <CopyButton
                      value={code}
                      onCopyClick={() =>
                        analyticsTrackEvents('affhub_copy_code.selected', {
                          linkType: LinkType.PREDEFINED,
                          linkIndex: index,
                          linkTargetUrl: code,
                        })
                      }
                    />
                  </UitkLayoutFlex>
                </div>
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </UitkCard>
    </UitkSpacing>
  );
}

export { ConfiguredQuicklinkV2 };
